import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AUTH_STATUS } from "components/Modals/AuthModal";
import { createContext, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { triggerCloseModal } from "store/slices/modals";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { triggerOpenModal } from "store/slices/modals";
import { APIFireAssessmentCandidateFeedbackSubmittedEvent, APIFireAssessmentInvitationOpenedEvent, APIGetAssessmentInvitationByID, APIGetInstanceByID, APIGetReservationByID, APIValidateToken } from "utils/api";
import { useInterval } from "utils/hooks";
import { authenticate } from "store/slices/auth";

// api assessment invitation status
export enum AssessmentInvitationStatus {
  CREATED = "created",
  SENT = "sent",
  OPENED = "opened",
  IN_PROGRESS = "in_progress",
  EXPIRED = "expired",
  FAILED = "failed",
  COMPLETED = "completed",
}

export enum AssessmentReservationStatus {
  CREATED = "created",
  SCHEDULED = "scheduled",
  PROVISIONING = "provisioning",
  READY = "ready",
  ENDED = "ended",
  CANCELLED = "cancelled",
  TERMINATED = "terminated",
  TERMINATING = "terminating",
  FAILED = "failed",
}

export enum PageState {
  LOADING = "loading",
  ERROR = "error",
  NOT_LOGGEDIN = "not_logged_in",
  NOT_INVITED = "not_invited",
  // api assessment invitation status
  INVITED = "invited",
  INPROGRESS = "in_progress",
  COMPLETED = "completed",
  FAILED = "failed",
  EXPIRED = "expired",
}

export const AssignmentsContext = createContext<any>({});

const AssignmentsProvider = ({ children }: any): any => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const isLoggedIn = useAppSelector((state) => state.auth.isLoggedIn);
  const [user, setUser] = useState(null);
  const [assessmentInvitationId, setAssessmentInvitationId] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [lab, setLab] = useState(null);
  const [instance, setInstance] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [searchParams] = useSearchParams();
  const [assessmentPageState, setAssessmentPageState] = useState<PageState>(PageState.LOADING);
  const [shouldShowAuthModal, setShouldShowAuthModal] = useState(false);
  const [assessmentInvitationOpened, setAssessmentInvitationOpened] = useState(false);
  // Reservation
  const [reservation, setReservation] = useState(null);
  const [fetchReservationRefreshRate, setFetchReservationRefreshRate] = useState<number | null>(null);
  const refreshReservation = () => {
    reservationQuery.refetch();
  };
  const refreshAssessmentInvitation = () => {
    assessmentInvitationQuery.refetch();
  };
  const refreshInstance = () => {
    instanceQuery.refetch();
  };

  /**
   * Set assessment invitation id from url params
   */
  useEffect(() => {
    // path will include ?id=assessment_invitation_id
    const aid = searchParams.get("id");
    console.log("UseEffect to set assessment invitation id", aid);
    if (aid) {
      setAssessmentInvitationId(aid);
      queryClient.invalidateQueries({ queryKey: ["assessment_invitation", aid] });
    } else {
      setAssessmentPageState(PageState.NOT_INVITED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    // if we are logged in but don't have user info, fetch it
    if (isLoggedIn && !user) {
      userQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  /**
   * Gets user info on app start
   */
  const userQuery = useQuery({
    queryKey: ["user_me"],
    queryFn: () => APIValidateToken()
  });

  useEffect(() => {
    if (userQuery.isSuccess) {
      console.log("UseEffect to set user", userQuery.data.data);
      setUser(userQuery.data.data);
      dispatch(authenticate(true, userQuery.data.data));
      setShouldShowAuthModal(false);
    } else if (userQuery.isError) {
      setShouldShowAuthModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userQuery.data]);

  const assessmentInvitationQuery = useQuery({
    queryKey: ["assessment_invitation", assessmentInvitationId],
    queryFn: () => APIGetAssessmentInvitationByID(assessmentInvitationId, true),
    enabled: !!assessmentInvitationId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (assessmentInvitationQuery.isSuccess) {
      console.log("UseEffect to set assessments - success", assessmentInvitationQuery.data.data);
      setSelectedAssessment(assessmentInvitationQuery.data.data);
      setOrganization(assessmentInvitationQuery.data.data.organisation);
      setLab(assessmentInvitationQuery.data.data.lab);

      // if we have assessment data but user isn't logged in, show the auth modal
      if (!isLoggedIn && !userQuery.isLoading) {
        setShouldShowAuthModal(true);
      }
    } else if (assessmentInvitationQuery.isError) {
      console.log("UseEffect to set assessments - error", assessmentInvitationQuery.error);
      setAssessmentPageState(PageState.NOT_INVITED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentInvitationQuery.data, assessmentInvitationId]);

  useEffect(() => {
    if ((assessmentInvitationQuery.isSuccess && assessmentInvitationQuery.data.data.user_id) && (user && user.id) && !assessmentInvitationOpened) {
      setAssessmentInvitationOpened(true);
      // EVENT ASSESSMENT INVITATION OPENED
      APIFireAssessmentInvitationOpenedEvent(assessmentInvitationQuery.data.data.id, "", user?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentInvitationQuery.data, isLoggedIn, user, assessmentInvitationOpened]);

  /**
   * Get reservation
   */
  const reservationQuery = useQuery({
    queryKey: ["reservation", selectedAssessment?.reservation_id],
    queryFn: () => APIGetReservationByID(selectedAssessment?.reservation_id),
    enabled: isLoggedIn && !!selectedAssessment?.reservation_id,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isLoggedIn && reservationQuery.isSuccess) {
      console.log("useEffect to fetch reservation", selectedAssessment.reservation_id);
      setReservation(reservationQuery.data.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, reservationQuery.data]);

  const instanceQuery = useQuery({
    queryKey: ["instance", reservation?.instance_id],
    queryFn: () => APIGetInstanceByID(reservation?.instance_id),
    enabled: isLoggedIn && !!reservation?.instance_id,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isLoggedIn && instanceQuery.isSuccess) {
      console.log("UseEffect to set instance", instanceQuery.data.data);
      setInstance(instanceQuery.data.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, instanceQuery.data, lab]);

  const fireCandidateFeedbackSubmittedEvent = (comment: string) => {
    APIFireAssessmentCandidateFeedbackSubmittedEvent(selectedAssessment?.id, user?.id, lab?.id, "null", comment);
  }

  /**
   * Auth modal management
   */
  const handleOpenAuthModal = () =>
    dispatch(triggerOpenModal("modal-auth", { status: AUTH_STATUS.ASSESSMENT_SIGNIN }));

  const handleCloseAuthModal = () => {
    dispatch(triggerCloseModal("modal-auth"));
    setShouldShowAuthModal(false);
  }

  /**
   * Show auth modal when needed
   */
  useEffect(() => {
    if (shouldShowAuthModal && !isLoggedIn) {
      handleOpenAuthModal();
    } else if (isLoggedIn) {
      handleCloseAuthModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowAuthModal, isLoggedIn]);

  /**
   * Polling mechanism for monitoring assessment reservation status
   */
  useInterval(
    async () => {
      if (!reservation) {
        return;
      }

      const isAssessmentInProgress = selectedAssessment.status === AssessmentInvitationStatus.IN_PROGRESS;
      if (!isAssessmentInProgress) {
        // stop polling if assessment is not in progress
        if (fetchReservationRefreshRate !== null) {
          setFetchReservationRefreshRate(null);
        }
        return;
      }

      // activate polling for intermediate states or ready state without instance_id
      if (fetchReservationRefreshRate === null &&
        (reservation.status === AssessmentReservationStatus.PROVISIONING ||
          reservation.status === AssessmentReservationStatus.SCHEDULED ||
          reservation.status === AssessmentReservationStatus.CREATED ||
          (reservation.status === AssessmentReservationStatus.READY && !reservation.instance_id))) {
        setFetchReservationRefreshRate(5000);
      }

      // handle other reservation states
      console.log("reservation status", reservation.status);
      switch (reservation.status) {
        case AssessmentReservationStatus.READY:
          if (reservation.instance_id) {
            console.log("reservation has instance_id, stop polling", reservation.instance_id);
            setFetchReservationRefreshRate(null);
          } else {
            console.warn("Reservation status is ready but instance_id is missing. Refreshing.");
            refreshReservation();
          }
          break;

        case AssessmentReservationStatus.FAILED:
          // stop polling on failure
          console.log("reservation failed, stopping polling", reservation);
          setFetchReservationRefreshRate(null);
          break;

        case AssessmentReservationStatus.PROVISIONING:
        case AssessmentReservationStatus.SCHEDULED:
        case AssessmentReservationStatus.CREATED:
          // continue polling for intermediate states
          console.log("reservation is provisioning, scheduled, or created, refreshing", reservation);
          refreshReservation();
          break;

        case AssessmentReservationStatus.ENDED:
        case AssessmentReservationStatus.CANCELLED:
        case AssessmentReservationStatus.TERMINATED:
        case AssessmentReservationStatus.TERMINATING:
          // stop polling for terminal states
          console.log("reservation is terminal, stopping polling", reservation);
          setFetchReservationRefreshRate(null);
          break;

        default:
          // handle unknown states by refreshing
          console.warn(`Unknown reservation status: ${reservation.status}. Refreshing.`);
          refreshReservation();
          break;
      }
    },
    // determine polling interval
    (() => {
      if (!reservation) {
        return 100;
      }
      console.log("reservation", reservation);
      const isAssessmentInProgress = selectedAssessment?.status === AssessmentInvitationStatus.IN_PROGRESS;

      // set polling interval based on conditions:
      // 1. assessment must be in progress
      // 2. reservation must be in intermediate state or ready without instance_id
      if (isAssessmentInProgress &&
        (reservation.status === AssessmentReservationStatus.PROVISIONING ||
          reservation.status === AssessmentReservationStatus.SCHEDULED ||
          reservation.status === AssessmentReservationStatus.CREATED ||
          (reservation.status === AssessmentReservationStatus.READY && !reservation.instance_id))) {
        return fetchReservationRefreshRate ?? 5000; // Use configured rate or default to 5s
      }
      return null; // Disable polling for other states
    })(),
  );

  /**
   * Set assessment page state based on assessment invitation status
   */
  useEffect(() => {
    const isLoading = userQuery.isLoading ||
      (assessmentInvitationId && assessmentInvitationQuery.isLoading) ||
      (isLoggedIn && selectedAssessment?.reservation_id && reservationQuery.isLoading);

    if (isLoading) {
      setAssessmentPageState(PageState.LOADING);
      return;
    }

    // Handle non-loggedin state
    if (!isLoggedIn) {
      if (!selectedAssessment) {
        setAssessmentPageState(PageState.NOT_INVITED);
      } else {
        setAssessmentPageState(PageState.INVITED);
      }
      return;
    }

    // Handle logged-in states
    if (!selectedAssessment) {
      setAssessmentPageState(PageState.NOT_INVITED);
    } else if (selectedAssessment.status === AssessmentInvitationStatus.CREATED ||
      selectedAssessment.status === AssessmentInvitationStatus.SENT ||
      selectedAssessment.status === AssessmentInvitationStatus.OPENED) {
      setAssessmentPageState(PageState.INVITED);
    } else if (selectedAssessment.status === AssessmentInvitationStatus.IN_PROGRESS) {
      // if assessment is in progress, reservation can be in terminal state (destroy is still ongoing)
      if (reservation && (reservation.status === AssessmentReservationStatus.ENDED || reservation.status === AssessmentReservationStatus.TERMINATING || reservation.status === AssessmentReservationStatus.TERMINATED)) {
        setAssessmentPageState(PageState.COMPLETED);
      } else {
        setAssessmentPageState(PageState.INPROGRESS);
      }
    } else if (selectedAssessment.status === AssessmentInvitationStatus.COMPLETED) {
      setAssessmentPageState(PageState.COMPLETED);
    } else if (selectedAssessment.status === AssessmentInvitationStatus.FAILED) {
      setAssessmentPageState(PageState.FAILED);
    } else if (selectedAssessment.status === AssessmentInvitationStatus.EXPIRED) {
      setAssessmentPageState(PageState.EXPIRED);
    } else {
      setAssessmentPageState(PageState.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssessment, isLoggedIn, userQuery.isLoading, assessmentInvitationQuery.isLoading, reservationQuery.isLoading]);

  return (
    <AssignmentsContext.Provider
      value={{
        user,
        isLoggedIn,
        organization,
        selectedAssessment,
        assessmentPageState,
        lab,
        reservation,
        isReservationLoaded: reservationQuery.isSuccess,
        refreshReservation,
        refreshAssessmentInvitation,
        refreshInstance,
        instance,
        isInstanceLoaded: instanceQuery.isSuccess,
        isInstanceError: instanceQuery.isError,
        isInstanceLoading: instanceQuery.isLoading,
        isInstanceFetching: instanceQuery.isFetching,
        handleOpenAuthModal: handleOpenAuthModal,
        handleCloseAuthModal: handleCloseAuthModal,
        fireCandidateFeedbackSubmittedEvent,
      }}
    >
      {children}
    </AssignmentsContext.Provider>
  );
};

export const useAssignmentsContext = () => useContext(AssignmentsContext);

export default AssignmentsProvider;